import { useEffect, useRef } from "react";

/**
 * Rising bubbles canvas — bubbles drift upward from the bottom, wobbling
 * slightly and fading out near the top. On-brand for "Deep Current" (water
 * theme) and completely different from firework bursts.
 */
export const RisingBubbles = ({ density = 1, className = "" }) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const state = { bubbles: [], size: { w: 0, h: 0 }, last: 0 };

        const resize = () => {
            const rect = canvas.getBoundingClientRect();
            state.size = { w: rect.width, h: rect.height };
            canvas.width = rect.width * dpr;
            canvas.height = rect.height * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };
        resize();
        const ro = new ResizeObserver(resize);
        ro.observe(canvas);

        // Bubble palette — soft teal + violet tints on top of transparency
        const palette = ["#2dd4bf", "#5eead4", "#a78bfa", "#22d3ee"];

        const spawn = () => {
            const { w, h } = state.size;
            const r = 2 + Math.random() * Math.random() * 22;
            state.bubbles.push({
                x: Math.random() * w,
                y: h + r + 4,
                r,
                vy: -(0.3 + Math.random() * 0.9) * (r < 10 ? 1.4 : 1),
                wobble: Math.random() * Math.PI * 2,
                wobbleSpeed: 0.008 + Math.random() * 0.02,
                wobbleAmp: 0.4 + Math.random() * 1.6,
                color: palette[Math.floor(Math.random() * palette.length)],
                alphaMax: 0.18 + Math.random() * 0.35,
            });
        };

        let rafId;
        const loop = (t) => {
            const { w, h } = state.size;

            // Trail-clearing wipe (leaves a soft ghost, then reset)
            ctx.clearRect(0, 0, w, h);

            // Auto-spawn based on density
            const spawnInterval = 140 / density;
            if (t - state.last > spawnInterval) {
                state.last = t;
                spawn();
                if (density > 1.2 && Math.random() > 0.6) spawn();
            }

            for (const b of state.bubbles) {
                b.wobble += b.wobbleSpeed;
                const x = b.x + Math.sin(b.wobble) * b.wobbleAmp;
                b.y += b.vy;

                // Fade in from bottom, fade out near top
                const fromBottom = (h - b.y) / h;
                const alpha = Math.min(b.alphaMax, Math.min(fromBottom * 2, (b.y / h) * 3));
                if (alpha <= 0) continue;

                // Bubble ring
                ctx.strokeStyle = b.color;
                ctx.globalAlpha = alpha;
                ctx.lineWidth = Math.max(1, b.r / 12);
                ctx.beginPath();
                ctx.arc(x, b.y, b.r, 0, Math.PI * 2);
                ctx.stroke();

                // Bubble highlight (small arc, top-left of the bubble)
                ctx.strokeStyle = "rgba(255,255,255,0.85)";
                ctx.globalAlpha = alpha * 0.6;
                ctx.lineWidth = Math.max(1, b.r / 14);
                ctx.beginPath();
                ctx.arc(x - b.r * 0.35, b.y - b.r * 0.35, b.r * 0.35, Math.PI, Math.PI * 1.5);
                ctx.stroke();
            }
            ctx.globalAlpha = 1;
            state.bubbles = state.bubbles.filter((b) => b.y + b.r > -10);

            rafId = requestAnimationFrame(loop);
        };
        rafId = requestAnimationFrame(loop);

        return () => {
            cancelAnimationFrame(rafId);
            ro.disconnect();
        };
    }, [density]);

    return (
        <canvas
            ref={canvasRef}
            aria-hidden="true"
            className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
        />
    );
};

export default RisingBubbles;
